import React, { useRef } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import { TransitionGroup, CSSTransition } from 'react-transition-group';

import Header from './components/Header';
import Footer from './components/Footer';
import ScrollToTop from './components/ScrollToTop';

import Home from './pages/Home/Home';
import About from './pages/About/About';
import Services from './pages/Services/Services';
import ServiceDetail from './pages/Services/ServiceDetail';
import Projects from './pages/Projects/Projects';
import FAQ from './pages/FAQ/FAQ';
import Contact from './pages/Contact/Contact';
import NotFound from './pages/NotFound/NotFound';
import ProjectDetail from './pages/Projects/ProjectDetail';

import Blogs from './pages/Blogs/Blogs';
import BlogDetail from './pages/Blogs/BlogDetail';
import BlogsByCategoryPage from './pages/Blogs/BlogsByCategoryPage';

import TermsAndConditions from './pages/TermsAndConditions/TermsAndConditions';
import PrivacyPolicy from './pages/PrivacyPolicy/PrivacyPolicy';
import Unsubscribe from './pages/Unsubscribe/Unsubscribe';

import ContactModal from './components/ContactModal';
import MetaTagsUpdater from './components/MetaTagsUpdater';
import ScriptInjector from './components/ScriptInjector';
import CacheInvalidator from './components/CacheInvalidator';

import { ToastProvider } from './contexts/ToastContext';

import './App.css';
import './Responsive.css';

function App() {
  const location = useLocation();
  const nodeRef = useRef(null);

  const getPageKey = (pathname) => {
    if (pathname === '/') return 'Home';
    if (pathname === '/about') return 'About';
    if (pathname === '/services') return 'Services';
    if (pathname.startsWith('/services/')) return 'ServiceDetail';
    if (pathname === '/projects') return 'Projects';
    if (pathname.startsWith('/projects/')) return 'ProjectDetail';
    if (pathname === '/blogs') return 'Blogs';
    if (pathname.startsWith('/blogs/')) return 'BlogDetail';
    if (pathname.startsWith('/category/')) return 'BlogsByCategory';
    if (pathname === '/faqs') return 'FAQ';
    if (pathname === '/contact') return 'Contact';
    if (pathname === '/terms-conditions') return 'TermsAndConditions';
    if (pathname === '/privacy-policy') return 'PrivacyPolicy';
    if (pathname.startsWith('/unsubscribe-newsletter/')) return 'Unsubscribe';

    return 'NotFound';
  };

  const pageKey = getPageKey(location.pathname);

  const [dynamicSeo, setDynamicSeo] = React.useState({
    title: '',
    description: '',
    image: '',
  });

  const [metaTagsReady, setMetaTagsReady] = React.useState(false);

  const handleMetaTagsComplete = React.useCallback(() => {
    setMetaTagsReady(true);
  }, []);

  React.useEffect(() => {
    setMetaTagsReady(false);
  }, [location.pathname]);

  return (
      <ToastProvider>
        <ScrollToTop />

        <MetaTagsUpdater
            pageKey={pageKey}
            dynamicTitle={dynamicSeo.title}
            dynamicDescription={dynamicSeo.description}
            dynamicImage={dynamicSeo.image}
            onComplete={handleMetaTagsComplete}
        />

        <ScriptInjector enabled={metaTagsReady} />

        <CacheInvalidator />

        <Header />

        <TransitionGroup component={null}>
          <CSSTransition key={location.pathname} timeout={300} classNames="page" nodeRef={nodeRef}>
            <div ref={nodeRef}>
              <Routes location={location}>
                <Route path="/" element={<Home setDynamicSeo={setDynamicSeo} />} />
                <Route path="/about" element={<About setDynamicSeo={setDynamicSeo} />} />
                <Route path="/services" element={<Services setDynamicSeo={setDynamicSeo} />} />
                <Route path="/services/:slug" element={<ServiceDetail setDynamicSeo={setDynamicSeo} />} />
                <Route path="/services/fullstack-web-dev" element={<ServiceDetail setDynamicSeo={setDynamicSeo} />} />
                <Route path="/services/api-development" element={<ServiceDetail setDynamicSeo={setDynamicSeo} />} />
                <Route path="/services/database-design" element={<ServiceDetail setDynamicSeo={setDynamicSeo} />} />
                <Route path="/services/cloud-integration" element={<ServiceDetail setDynamicSeo={setDynamicSeo} />} />
                <Route path="/services/performance-tuning" element={<ServiceDetail setDynamicSeo={setDynamicSeo} />} />
                <Route path="/projects" element={<Projects setDynamicSeo={setDynamicSeo} />} />
                <Route path="/projects/:slug" element={<ProjectDetail setDynamicSeo={setDynamicSeo} />} />
                <Route path="/blogs" element={<Blogs setDynamicSeo={setDynamicSeo} />} />
                <Route path="/blogs/:slug" element={<BlogDetail setDynamicSeo={setDynamicSeo} />} />
                <Route path="/category/:slug" element={<BlogsByCategoryPage setDynamicSeo={setDynamicSeo} />} />
                <Route path="/faqs" element={<FAQ setDynamicSeo={setDynamicSeo} />} />
                <Route path="/contact" element={<Contact setDynamicSeo={setDynamicSeo} />} />
                <Route path="/terms-conditions" element={<TermsAndConditions setDynamicSeo={setDynamicSeo} />} />
                <Route path="/privacy-policy" element={<PrivacyPolicy setDynamicSeo={setDynamicSeo} />} />
                <Route path="/unsubscribe-newsletter/:token" element={<Unsubscribe />} />
                <Route path="*" element={<NotFound setDynamicSeo={setDynamicSeo} />} />
              </Routes>
            </div>
          </CSSTransition>
        </TransitionGroup>
        <Footer />
        <ContactModal />
      </ToastProvider>
  );
}

export default App;